<template>
  <div class="alert-scheduler">
    <div class="alert-card">
      <div class="alert-card-header">
        <div>
          <h3>Automated Alerts</h3>
          <p>
            Send clock-off reminders if a worker hasn't clocked out by the
            cutoff time.
          </p>
        </div>
        <b-button
          variant="outline-primary"
          size="sm"
          :disabled="saving"
          @click="toggleForm"
        >
          {{ showForm ? 'Cancel' : '+ Add Alert' }}
        </b-button>
      </div>

      <div v-if="alertSchedules.length === 0 && !showForm" class="alert-empty">
        <b-icon-bell class="empty-icon" />
        <p>No alerts configured yet.</p>
      </div>

      <div v-else-if="alertSchedules.length > 0" class="table-wrap">
        <b-table
          :items="alertSchedules"
          :fields="fields"
          responsive
          hover
          class="alert-table mb-0"
        >
          <template #cell(cutoff_time)="{ item }">
            <span class="cutoff-time">{{ item.cutoff_time }}</span>
          </template>
          <template #cell(project)="{ item }">
            {{ projectLabel(item.project_id) }}
          </template>
          <template #cell(message)="{ item }">
            <span class="alert-message">{{ item.message }}</span>
          </template>
          <template #cell(actions)="{ item }">
            <b-button
              size="sm"
              variant="outline-danger"
              @click="removeSchedule(item.id)"
            >
              Remove
            </b-button>
          </template>
        </b-table>
      </div>

      <b-collapse v-model="showForm">
        <div class="alert-form">
          <h4 class="form-title">New Alert</h4>
          <b-row>
            <b-col cols="12" md="4">
              <b-form-group label="Cutoff Time" label-class="form-label-sm">
                <b-form-input
                  v-model="form.cutoff_time"
                  type="time"
                  size="sm"
                  required
                />
              </b-form-group>
            </b-col>
            <b-col cols="12" md="4">
              <b-form-group
                label="Project (optional)"
                label-class="form-label-sm"
              >
                <b-form-select
                  v-model="form.project_id"
                  :options="projectOptions"
                  size="sm"
                />
              </b-form-group>
            </b-col>
            <b-col cols="12" md="4">
              <b-form-group label="Message" label-class="form-label-sm">
                <b-form-input
                  v-model="form.message"
                  size="sm"
                  placeholder="e.g. Please remember to clock off"
                  required
                />
              </b-form-group>
            </b-col>
          </b-row>
          <b-button
            type="button"
            variant="primary"
            size="sm"
            :disabled="saving"
            @click="submitForm"
          >
            {{ saving ? 'Saving…' : 'Save Alert' }}
          </b-button>
        </div>
      </b-collapse>
    </div>
  </div>
</template>

<script>
import { sameProjectId } from '@/utils/timesheetHelpers'

export default {
  name: 'DashboardAlertScheduler',
  data() {
    return {
      showForm: false,
      saving: false,
      form: { cutoff_time: '', message: '', project_id: null },
      fields: [
        { key: 'cutoff_time', label: 'Cutoff Time', thStyle: 'width: 120px' },
        { key: 'project', label: 'Project', thStyle: 'width: 160px' },
        { key: 'message', label: 'Message' },
        { key: 'actions', label: '', thStyle: 'width: 100px' },
      ],
    }
  },
  computed: {
    alertSchedules() {
      return this.$store.state.timesheet.alertSchedules
    },
    projectOptions() {
      return this.$store.getters['timesheet/projectOptions']
    },
  },
  methods: {
    toggleForm() {
      if (this.saving) return
      this.showForm = !this.showForm
    },
    projectLabel(projectId) {
      if (!projectId) return 'All Projects'
      const match = (this.projectOptions || []).find(function (p) {
        return sameProjectId(p.value, projectId)
      })
      return match ? match.text : 'Project #' + projectId
    },
    async removeSchedule(id) {
      try {
        await this.$store.dispatch('timesheet/deleteAlertSchedule', id)
        this.$bvToast.toast('Alert removed.', {
          title: 'Success',
          variant: 'success',
          solid: true,
        })
      } catch (e) {
        this.$bvToast.toast('Failed to remove alert.', {
          title: 'Error',
          variant: 'danger',
          solid: true,
        })
      }
    },
    async submitForm() {
      if (!this.form.cutoff_time || !this.form.message) {
        this.$bvToast.toast('Please enter a cutoff time and message.', {
          title: 'Validation',
          variant: 'warning',
          solid: true,
        })
        return
      }
      this.saving = true
      try {
        await this.$store.dispatch('timesheet/createAlertSchedule', {
          ...this.form,
        })
        this.form = { cutoff_time: '', message: '', project_id: null }
        this.showForm = false
        this.$bvToast.toast('Alert saved.', {
          title: 'Success',
          variant: 'success',
          solid: true,
        })
      } catch (e) {
        this.$bvToast.toast('Failed to save alert.', {
          title: 'Error',
          variant: 'danger',
          solid: true,
        })
      } finally {
        this.saving = false
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.alert-scheduler {
  margin-top: 16px;
}

.alert-card {
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  overflow: hidden;
}

.alert-card-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  padding: 14px 16px;
  border-bottom: 1px solid #f3f4f6;
  background: #fafafa;

  h3 {
    margin: 0;
    font-size: 15px;
    font-weight: 600;
    color: #111827;
  }

  p {
    margin: 4px 0 0;
    font-size: 12px;
    color: #6b7280;
    max-width: 480px;
  }
}

.alert-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 40px 24px;
  color: #9ca3af;

  p {
    margin: 10px 0 0;
    font-size: 13px;
  }
}

.empty-icon {
  font-size: 1.75rem;
  color: #d1d5db;
}

.table-wrap {
  overflow-x: auto;
}

.cutoff-time {
  font-weight: 600;
  color: #111827;
}

.alert-message {
  color: #374151;
  font-size: 13px;
}

.alert-form {
  padding: 16px 24px;
  border-top: 1px solid #f3f4f6;
  background: #f9fafb;
}

.form-title {
  margin: 0 0 14px;
  font-size: 13px;
  font-weight: 600;
  color: #374151;
}

/deep/ .form-label-sm {
  font-size: 11px;
  font-weight: 600;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.03em;
}

/deep/ .alert-form {
  .form-control {
    font-size: 13px;
    border-color: #d1d5db;
    border-radius: 6px;
  }
}

/deep/ .alert-table {
  thead th {
    font-size: 11px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.03em;
    color: #6b7280;
    border-top: none;
    border-bottom: 1px solid #e5e7eb;
    background: #fff;
    padding: 10px 14px;
  }

  tbody td {
    padding: 12px 14px;
    vertical-align: middle;
    border-color: #f3f4f6;
    font-size: 13px;
  }

  tbody tr:hover {
    background: #f9fafb;
  }
}
</style>
